import { useMemo } from 'react';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';

import { getRoadmapInitiativesWithFields } from '@/features/canvas/api/initiative';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';

import { getInitiativeDates } from '../utils/getInitiativeDates';

export const useTimelineBounds = () => {
  const { filteredNodes, roadmap, isPublicView, hiddenChildNodeIds } = useEditorContext();

  // Fetch initiatives with fields for date information
  const { data: initiativesWithFields } = useQuery(
    ['initiatives-with-fields', roadmap.id],
    () => getRoadmapInitiativesWithFields({ roadmapId: roadmap.id! }),
    {
      enabled: !!roadmap.id && !isPublicView,
      onError: error => {
        console.error('Failed to load initiatives with fields:', error);
      },
    }
  );

  // Create enhanced filtered nodes with complete initiative data including fields
  const enhancedFilteredNodes = useMemo(() => {
    // Filter out nodes where isVisible is explicitly false (eye-icon hidden)
    // and nodes that are in hiddenChildNodeIds (KON-145 hidden children)
    const visibleFilteredNodes = filteredNodes.filter(
      n => n.isVisible !== false && !(n.id !== undefined && hiddenChildNodeIds.has(n.id))
    );

    // In public view, the initiative data is already in filteredNodes
    if (isPublicView || !initiativesWithFields) return visibleFilteredNodes;

    return visibleFilteredNodes.map(node => {
      if (!node.initiatives) return node;

      // Find the corresponding initiative with fields
      const initiativeWithFields = initiativesWithFields.find(
        initiative => initiative.id === node.initiatives.id
      );

      if (initiativeWithFields) {
        return {
          ...node,
          initiatives: initiativeWithFields,
        };
      }

      return node;
    });
  }, [filteredNodes, initiativesWithFields, isPublicView, hiddenChildNodeIds]);

  // Calculate timeline bounds from initiative dates
  const timelineBounds = useMemo(() => {
    if (!enhancedFilteredNodes.length) {
      const now = dayjs();
      return {
        startDate: now.startOf('month'),
        endDate: now.add(36, 'months').endOf('month'),
      };
    }

    // Get all dates from initiatives (both start and end dates)
    const allDates: dayjs.Dayjs[] = [];

    enhancedFilteredNodes.forEach(node => {
      const { startDate, endDate } = getInitiativeDates(node.initiatives);
      if (startDate) allDates.push(dayjs(startDate));
      if (endDate) allDates.push(dayjs(endDate));
    });

    if (!allDates.length) {
      const now = dayjs();
      return {
        startDate: now.startOf('month'),
        endDate: now.add(36, 'months').endOf('month'),
      };
    }

    // Find the earliest and latest dates
    const earliestDate = allDates.reduce(
      (min, date) => (date.isBefore(min) ? date : min),
      allDates[0]
    );

    const latestDate = allDates.reduce(
      (max, date) => (date.isAfter(max) ? date : max),
      allDates[0]
    );

    // Timeline starts 2 months before the earliest date and ends at the end of the latest date
    const timelineStart = earliestDate.startOf('month').subtract(2, 'months');
    const timelineEnd = latestDate.endOf('month');

    return {
      startDate: timelineStart,
      endDate: timelineEnd,
    };
  }, [enhancedFilteredNodes]);

  return {
    timelineBounds,
    enhancedFilteredNodes,
    initiativesWithFields,
  };
};
